<template>
  <div class="main">
    <div
      class="errands"
      key="loaded"
    >
      <div
        class="table"
        ref="table"
      >
        <div
          class="table__header"
          ref="sortList"
          :class="{ loading }"
        >

          <div
            class="table__col sortable"
            :class="getSortClasses('address')"
            @click="sort($event, 'address')"
          >{{ $t('errands.name') }}</div>

          <div
            class="table__col sortable"
            :class="getSortClasses('prpm_last_name')"
            @click="sort($event, 'prpm_last_name')"
          >{{ $t('errands.propertyManager') }}</div>

          <div
            class="table__col sortable"
            :class="getSortClasses('created_date')"
            @click="sort($event, 'created_date')"
          >{{ $t('errands.created') }}</div>

          <div
            class="table__col sortable"
            :class="getSortClasses('work_type')"
            @click="sort($event, 'work_type')"
          >{{ $t('errands.damageType') }}</div>

          <div class="table__col">{{ $t('errands.status') }} </div>

          <div class="table__col">&nbsp;</div>
        </div>
        <div
          class="empty__result"
          v-if="noSearchResult"
        >
          {{ $t('errands.noErrandsFound') }}
        </div>
        <div
          v-else
          class="table__data"
        >
          <div v-if="!loading">
            <router-link
              class="table__row"
              v-for="(errand, i) in items"
              :to="{ path: `/${errand.request_id}` }"
              :key="`${errand.request_id}_${i}`"
            >

              <div class="table__col">
                <div class="errand__address">
                  <div>{{ errand.address }}</div>
                  <div>{{ errand.housing_cooperative }} </div>
                  <div>{{ errand.request_id }} {{ errand.claim_no }}</div>
                </div>
                <div>
                  <small>{{ errand.name }}</small>
                </div>
              </div>

              <div class="table__col">{{ errand.prpm_last_name }}</div>

              <div class="table__col">{{ formatDate(errand.created_date, 'll') }}</div>

              <div class="table__col">
                {{ getWorkType(errand) }}
                <span :style="getColorDotStyle(errand)"></span>
              </div>

              <div class="table__col">
                <div class="errand__type">{{ getErrandStatus(errand) }}</div>
              </div>

              <div class="table__col">
                <div class="errand__icon"></div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="loading"
      class="loader center"
      key="loading"
    >
      <v-progress-circular
        size="70"
        width="5"
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>
    <div class="text-center">
      <v-pagination
        v-if="totalRows > 0 && !loading"
        v-model="selectedPage"
        :length="totalPages"
        :total-visible="7"
        :disabled="loading"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import repository from '@/core/repository/entity-repository'
import entityList from '@/mixins/entity-list'
import _ from 'lodash'
import errandService from '@/core/services/errand-service'
import utils from '@/core/utils'
import { loadUnits } from '@/core/startup'

export default {
  data: () => ({
    pendingXhr: null,
    selectedPage: 1,
    pageSize: 10,
    totalRows: 0,
    sortByOrder: 'dsc',
    sortBy: 'created_date'
  }),
  mixins: [entityList],
  methods: {
    getErrandStatus: errandService.getErrandStatus,
    tryAbortPendingSearch() {
      _.get(this, 'pendingXhr.abort', _.noop)()
    },
    loadErrands() {
      this.tryAbortPendingSearch()
      this.loading = true
      const searchText = utils.replaceSpacesWithWildcards(
        this.selectedSearchFilters.searchText
      )
      const workTypes =
        this.selectedSearchFilters.workTypes.length > 0
          ? this.selectedSearchFilters.workTypes.join('|')
          : '*'
      this.pendingXhr = repository
        .getErrands(
          this.selectedSearchFilters.propertyManagerFilter,
          searchText,
          this.selectedSearchFilters.caseStatus,
          workTypes,
          this.pageSize,
          this.selectedPage,
          this.sortByOrder,
          this.sortBy,
          this.username
        )
        .done(data => {
          this.totalRows = data.totalCount
          this.items = data.outputTable
        })
        .fail(() => {
          this.totalRows = 0
          this.items = []
        })
        .always(() => {
          this.loading = false
        })
    },
    sort(e, sortBy) {
      this.sortBy = sortBy
      this.sortByOrder = this.sortByOrder === 'dsc' ? 'asc' : 'dsc'
    },
    getSortClasses(sortBy) {
      return (
        (this.sortBy === sortBy ? 'sort--active ' : '') +
        `sort--${this.sortByOrder}`
      )
    },
    getWorkType(errand) {
      return _.get(this.workTypes, [errand.work_type_id, 'description'])
    },
    getColorDotStyle(errand) {
      return {
        display: 'inline-block',
        height: '10px',
        width: '10px',
        borderRadius: '50%',
        backgroundColor: `${errand.workTypeColor}`
      }
    }
  },
  created() {
    this.loadErrands()
    loadUnits()
  },
  computed: {
    sortOrFilterChanged() {
      return [
        this.sortBy,
        this.sortByOrder,
        this.pageSize,
        this.selectedPage,
        this.selectedSearchFilters,
        _.now()
      ]
    },
    resetPageChanged() {
      return [this.pageSize, this.selectedSearchFilters, _.now()]
    },
    noSearchResult() {
      return !this.loading && this.items.length === 0
    },
    workTypes() {
      return _.keyBy(this.$store.getters.workTypes, 'id')
    },
    selectedSearchFilters() {
      return this.$store.getters.searchFilters
    },
    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize)
    }
  },
  watch: {
    sortOrFilterChanged: {
      deep: true,
      handler() {
        this.loadErrands()
      }
    },
    resetPageChanged: {
      deep: true,
      handler() {
        this.selectedPage = 1
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.loader {
  margin: measure(m);
}

.loading {
  color: color(white);
  opacity: 0.3;
  pointer-events: none;
}

.overlay__loader {
  position: absolute;
  z-index: 10;
  top: 15rem;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
}

.empty__result {
  padding: measure(m);
  text-align: center;
}

.active {
  font-weight: bold;
}

.errands {
  @include layout-box;
  margin: measure(m);
}

.errand__address {
  font-weight: $font-weight-medium;
}

.errand__type {
  display: inline-block;
  color: color(tertiary);
}

.errand__icon {
  display: inline-block;

  &:before {
    @extend .icon-before;
    content: $icon-right;
    color: color(primary);
  }
}

.button {
  @include font-size(s);
  font-weight: normal;
  border-radius: 100%;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  margin-right: measure(xs) / 2;
}

.pagination {
  margin: measure(s);
}

.table {
  position: relative;
  width: 100%;
}

.table__row {
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  flex: 0 0 100%;
  align-items: center;
  width: 100%;
  text-decoration: none;
  border-bottom: 0.0625rem solid color(light);
  color: color(jet);

  &:last-child {
    border-bottom: none;
  }

  .no-touch &:not(.table__header):hover {
    background-color: color(light);
  }

  @supports (grid-gap: 1rem) {
    @media #{$mq-to-medium} {
      display: grid;
      grid-template-areas: "project type" "project date";
      grid-template-columns: 60% 40%;
    }
  }
}

.table__header {
  @extend .table__row;

  background-color: color(white);

  .table__col {
    text-transform: uppercase;
    white-space: nowrap;
    color: #57677f;
    @include font-size(s);
  }
}

$column-widths-mobile: (60%, 0, 40%, 0, 40%, 0);
$column-widths: (30%, 22%, 14%, 16%, 13%, 5%);

.table__col {
  position: relative;

  padding: measure(m);
  flex: 0 1 100%;

  &:last-child {
    text-align: right;
  }

  $index: 1;
  @each $width in $column-widths {
    &:nth-child(#{$index}) {
      flex: 1 1 $width;
      width: $width;
      $index: $index + 1;
      content: $index;
    }
  }
}

@media #{$mq-mobile-only} {
  .errands {
    background: none;
  }

  .table__header {
    display: none;
  }

  .table__row {
    @include layout-box;
    border-bottom: none;
    margin-bottom: measure(xs);
    align-items: flex-start;
  }

  .table__col:nth-child(5) {
    text-align: right;
  }

  .table__col {
    $index: 1;
    @each $width in $column-widths-mobile {
      &:nth-child(#{$index}) {
        flex: 1 1 $width;
        width: $width;

        @if ($width == 0) {
          display: none;
        }

        $index: $index + 1;
      }
    }
  }
}

.table__col:nth-child(1) {
  @supports (grid-gap: 1rem) {
    @media #{$mq-to-medium} {
      grid-area: project;
      width: 100%;
    }
  }
}

.table__col:nth-child(3) {
  @supports (grid-gap: 1rem) {
    @media #{$mq-to-medium} {
      grid-area: date;
      width: 100%;
      padding-top: 0;
      text-align: right;
    }
  }
}

.table__col:nth-child(5) {
  @supports (grid-gap: 1rem) {
    @media #{$mq-to-medium} {
      grid-area: type;
      width: 100%;
    }
  }
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: all 300ms;

  .no-touch &:hover {
    color: color(primary);
  }

  &.sort--active {
    color: color(primary);
  }

  &:after {
    @extend .icon-after;
    display: none;
    margin-left: 0.625rem;
    font-size: 11px;
  }
  &.sort--active.sort--asc:after {
    content: $icon-sort-asc;
    display: inline-block;
  }
  &.sort--active.sort--dsc:after {
    content: $icon-sort-desc;
    display: inline-block;
  }
}

.load-more {
  display: flex;
  margin: 0 auto;
  width: auto;
}

.main {
  padding: 0;
}

</style>
